<template>
  <chart :option="calOptions" ref="chart"></chart>
</template>

<script>
import dayjs from 'dayjs';
import { merge } from 'lodash';
import Chart from '@/components/echarts/chart.vue';
import { optionGen, getLegendKey } from '@/utils/helper';

export default {
  components: {
    Chart,
  },
  props: {
    options: {
      type: Object,
      required: true,
    },
    unit: {
      type: String,
      default: 'day',
    },
    chartOptions: {
      type: Object,
      required: false,
    },
    dateKeys: {
      type: Object,
      required: true,
    },
    legendData: {
      type: Array,
      required: true,
    },
    showCompareDate: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    calOptions() {
      const thisOptions = this.options;
      const { baseDateKeys, compareDateKeys } = this.dateKeys;
      const { legendData, unit } = this;
      const filterLegendData = legendData.filter(item => !!item.show);

      const options = optionGen();
      options.color = filterLegendData.map(item => item.color);

      const xAxis = {
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          lineStyle: {
            color: '#E3EAEE',
          },
        },
        type: 'value',
      };
      const yAxis = {
        type: 'category',
        axisTick: {
          alignWithLabel: true,
        },
        data: [],
      };
      options.xAxis = xAxis;
      options.yAxis = yAxis;
      options.grid.bottom = 0;
      options.grid.top = 0;
      options.grid.right = '4%';
      if (this.showCompareDate) {
        options.tooltip.formatter = function (params) {
          // console.log('params', params);

          function makeRowHtml() {
            let str = '';
            filterLegendData.forEach(item => {
              const seriesName = getLegendKey(item);
              const seriesItem = params.find(p => p.seriesName === seriesName);
              if (!seriesItem) {
                return;
              }
              const seriesItemCompare = params.find(p => p.seriesId === `${seriesItem.seriesId}-compare`);

              // console.log('item', item, seriesItem);
              str = `${str}<tr>
                <td title="${item.desc}">
                  <div style="max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;display: flex;align-items: center;">
                    <span style="display:inline-block;margin-right: 6px;color:${item.color};">
                      ${item.zhibiaoIdxChar}${item.dzzIdxChar}
                    </span>
                    ${item.desc}
                  </div>
                </td>
                <td style="padding: 0 20px 0 20px;">${seriesItem ? seriesItem.data.toLocaleString() : '-'}</td>
                <td style="padding: 0 20px 0 20px;">${
                  seriesItemCompare ? seriesItemCompare.data.toLocaleString() : '-'
                }</td>
              </tr>`;
            });
            return str;
          }
          let axisValueArr = [];
          let axisValue = '';
          if (params.length) {
            axisValue = params[0].axisValue;
            axisValueArr = axisValue.split('/');
          }
          return `
          <div style="max-height: 300px; overflow: auto;">
          <table>
            <thead>
              <tr>
                <td></td><td style="padding: 0 20px 0 20px;">${
                  axisValueArr[0]
                }</td><td style="padding: 0 20px 0 20px;">${axisValueArr[1]}</td>
              </tr>
            </thead>
            <hbody>
              ${makeRowHtml()}
            </hbody>
          </table></div>
          `;
        };
      } else {
        options.tooltip.formatter = function (params) {
          function makeRowHtml() {
            let str = '';
            filterLegendData.forEach(item => {
              const seriesName = getLegendKey(item);
              const seriesItem = params.find(p => p.seriesName === seriesName);
              if (!seriesItem) {
                return;
              }
              // console.log('item', item, seriesItem);
              str = `${str}<tr>
                <td title="${item.desc}">
                  <div style="max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;display: flex;align-items: center;">
                    <span style="display:inline-block;margin-right: 6px;color:${item.color};">
                      ${item.zhibiaoIdxChar}${item.dzzIdxChar}
                    </span>
                    ${item.desc}
                  </div>
                </td>
                <td style="padding: 0 20px 0 20px;">${seriesItem ? seriesItem.data.toLocaleString() : '-'}</td>
              </tr>`;
            });
            return str;
          }
          let axisValue = '-';
          if (params.length) {
            axisValue = params[0].axisValue;
          }
          return `
          <div style="max-height: 300px; overflow: auto;">
          <table>
            <thead>
              <tr>
                <td>${axisValue}</td><td></td>
              </tr>
            </thead>
            <hbody>
              ${makeRowHtml()}
            </hbody>
          </table></div>
          `;
        };
      }

      baseDateKeys.forEach((bdk, idx) => {
        let val = dayjs(bdk).format(unit === 'day' ? 'MM-DD' : 'MM-DD HH:mm');
        if (compareDateKeys) {
          val = `${val}/${dayjs(compareDateKeys[idx]).format(unit === 'day' ? 'MM-DD' : 'MM-DD HH:mm')}`;
        }
        options.yAxis.data.push(val);
      });

      // console.log('filterLegendData', filterLegendData);
      const { legend } = options;
      filterLegendData.forEach((item, idx) => {
        const seriesName = getLegendKey(item);
        const seriesItem = thisOptions.seriesMap.find(ti => ti.uniqKey === item.name);
        if (seriesItem) {
          const data = [];

          // console.log('seriesItem.target_name', seriesItem.target_name);
          baseDateKeys.forEach(bdk => {
            const findObj = seriesItem.list.find(i => String(i.date) === String(bdk)) || { date: bdk, val: '-' };
            data.push(findObj);
          });
          legend.icon = 'circle';
          legend.itemHeight = 10;
          legend.itemWidth = 10;
          legend.bottom = 0;
          // legend.data.push(seriesItem.uniqKey.replace(/---/g, ','));
          legend.data.push(seriesName);
          options.series.push({
            id: `${idx}`,
            // name: seriesItem.uniqKey.replace(/---/g, ','),
            name: seriesName,
            type: 'bar',
            stack: seriesItem.target_name,
            color: item.color,
            data: data.map(i => i.val),
          });
        }

        if (this.showCompareDate) {
          const seriesItemCompare = thisOptions.seriesMapCompare.find(ti => ti.uniqKey === item.name);
          if (seriesItemCompare) {
            const dataCompare = [];
            compareDateKeys.forEach(bdk => {
              const findObj = seriesItemCompare.list.find(i => String(i.date) === String(bdk)) || {
                date: bdk,
                val: '-',
              };
              dataCompare.push(findObj);
            });
            // console.log("dataCompare",dataCompare)
            // legend.data.push(seriesItemCompare.uniqKey.replace(/---/g, ','));
            legend.data.push(seriesName);
            options.series.push({
              id: `${idx}-compare`,
              // name: `${seriesItemCompare.uniqKey.replace(/---/g, ',')}`,
              name: seriesName,
              type: 'bar',
              stack: `${seriesItem.target_name}compare`,
              color: `${item.color.replace(/,1\)/g, ',0.5)')}`,
              data: dataCompare.map(i => i.val),
            });
          }
        }
      });
      if (this.chartOptions) {
        merge(options, this.chartOptions);
      }
      // console.log('stackedbar options1', options);
      return options;
    },
  },
  data() {
    return {
      //
    };
  },
};
</script>
